import { createSlice } from "@reduxjs/toolkit";
import {
  DEFAULT_ERROR_TEXT,
  DOWNLOAD_TYPES,
  INITIAL_ERROR_STATE,
  LARGE_DOWNLOAD_MESSAGES_COUNT,
  MESSAGE_STATUSES,
  MESSAGES_ADD_DIRECTIONS,
  REQUEST_STATUSES,
  SMALL_DOWNLOAD_MESSAGES_COUNT,
  STORE_SLICES,
} from "../../../packages/constants";
import { helpers } from "../../../packages/helpers";
import { isMessageError } from "../../../packages/helpers/predicates";
import {
  addMessages,
  decreaseUnreadCount,
  getChatInfo,
  getChatSettings,
  getFile,
  getMessages,
  getMessagesByReply,
  getNextMessages,
  getPrevMessages,
  handleChangeInputMessage,
  increaseUnreadCount,
  postChatInfo,
  postEventMessageReading,
  postEventUserTyping,
  postMessageFile,
  postMessageText,
  readMessage,
  resetError,
  resetLastReadMessageIndex,
  resetOpponentLastTypingTime,
  resetReplyMessageId,
  resetStatus,
  resetStore,
  resetUnreadCount,
  setOperatorName,
  setOpponentLastTypingTime,
  setReplyMessageId,
  setTabTitleUpdateIntervalId,
  updateFileMessage,
  updateMessageStatuses,
} from "../actions";
import { isMessageFile } from "../type-guards";
import { CHAT_ENDPOINT_NAMES } from "../types";
import { initialState } from "./initial-state";

const { isMessageReceived } = helpers.predicates;
const { getLastIndex } = helpers.array;

const chatSlice = createSlice({
  name: STORE_SLICES.CHAT,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //base
    builder.addCase(resetStore, () => initialState);
    builder.addCase(resetStatus, (state, action) => {
      const { endpoint } = action.payload;

      if (endpoint === "all") {
        state.requestStatuses = initialState.requestStatuses;
      } else {
        state.requestStatuses[endpoint] = REQUEST_STATUSES.PENDING;
      }
    });
    builder.addCase(resetError, (state, action) => {
      const { endpoint } = action.payload;

      state.requestStatuses[endpoint] = REQUEST_STATUSES.PENDING;
      state.errorMessages[endpoint] = INITIAL_ERROR_STATE;
    });

    builder.addCase(increaseUnreadCount, (state) => {
      state.settings.unreadCount += 1;
    });
    builder.addCase(decreaseUnreadCount, (state) => {
      if (state.settings.unreadCount > 0) {
        state.settings.unreadCount -= 1;
      }
    });
    builder.addCase(resetUnreadCount, (state) => {
      state.settings.unreadCount = initialState.settings.unreadCount;
    });

    builder.addCase(resetOpponentLastTypingTime, (state) => {
      state.settings.opponentLastTypingTime =
        initialState.settings.opponentLastTypingTime;
    });

    builder.addCase(resetLastReadMessageIndex, (state) => {
      state.settings.lastReadMessageIndex =
        initialState.settings.lastReadMessageIndex;
    });

    // Ввод сообщения
    builder.addCase(handleChangeInputMessage, (state, action) => {
      const { text } = action.payload;

      state.message.text = text;
    });

    // Добавление сообщения
    builder.addCase(addMessages, (state, action) => {
      const { messages, direction, authors = {} } = action.payload;

      state.authors = { ...state.authors, ...authors };

      if (direction === MESSAGES_ADD_DIRECTIONS.START) {
        state.messages = [...messages, ...state.messages];
      }

      if (direction === MESSAGES_ADD_DIRECTIONS.END) {
        state.messages = [...state.messages, ...messages];
      }

      if (direction === MESSAGES_ADD_DIRECTIONS.INSIDE) {
        messages.forEach((message) => {
          const messageIndex = state.messages.findIndex(
            ({ traceId }) => message.traceId === traceId
          );

          if (messageIndex > -1) {
            state.messages[messageIndex] = message;
          } else {
            state.messages = [...state.messages, message];
          }
        });
      }
    });

    builder.addCase(readMessage, (state, action) => {
      const { messageId } = action.payload;

      state.messages.forEach((message) => {
        if (message.id === messageId) {
          message.status = MESSAGE_STATUSES.READ;
        }
      });
    });

    builder.addCase(setReplyMessageId, (state, action) => {
      const { messageId } = action.payload;

      state.message.replyMessageId = messageId;
    });

    builder.addCase(resetReplyMessageId, (state) => {
      state.message.replyMessageId = initialState.message.replyMessageId;
    });

    // Запрос информации о чате
    builder.addCase(getChatInfo.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_INFO] =
        REQUEST_STATUSES.LOADING;
      state.settings.isChatIdRequestPending = true;
    });
    builder.addCase(getChatInfo.fulfilled, (state, action) => {
      const { id } = action.payload;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_INFO] =
        REQUEST_STATUSES.SUCCESS;
      state.id = id;
      state.settings.isChatIdRequestPending = false;
    });
    builder.addCase(getChatInfo.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_INFO] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_CHAT_INFO] = { type, status };
      state.settings.isChatIdRequestPending = false;
    });

    builder.addCase(postChatInfo.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_CHAT_INFO] =
        REQUEST_STATUSES.LOADING;
      state.settings.isChatIdRequestPending = true;
    });
    builder.addCase(postChatInfo.fulfilled, (state, action) => {
      const { id } = action.payload;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_CHAT_INFO] =
        REQUEST_STATUSES.SUCCESS;
      state.id = id;
      if (id) {
        state.settings.isChatIdRequestPending = false;
      }
    });
    builder.addCase(postChatInfo.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_CHAT_INFO] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.POST_CHAT_INFO] = {
        type,
        status,
      };
      state.settings.isChatIdRequestPending = false;
    });

    // Запрос настроек чата (используется для long polling)
    builder.addCase(getChatSettings.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_SETTINGS] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(getChatSettings.fulfilled, (state, action) => {
      const { unreadCount, pollingTimeout } = action.payload;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_SETTINGS] =
        REQUEST_STATUSES.SUCCESS;
      state.settings.unreadCount = unreadCount;
      state.settings.pollingTimeout = pollingTimeout;
    });
    builder.addCase(getChatSettings.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_CHAT_SETTINGS] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_CHAT_SETTINGS] = {
        type,
        status,
      };
    });

    // Получение списка сообщений
    builder.addCase(getMessages.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(getMessages.fulfilled, (state, action) => {
      const { messages, authors, lastReadMessageIndex } = action.payload;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES] =
        REQUEST_STATUSES.SUCCESS;

      /*
        отключаем подгрузку прошлых сообщений потому что их на бекенде больше нет;
      */
      if (messages.length < LARGE_DOWNLOAD_MESSAGES_COUNT) {
        state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] =
          REQUEST_STATUSES.SUCCESS;
      }

      if (lastReadMessageIndex > state.settings.lastReadMessageIndex) {
        state.settings.lastReadMessageIndex = lastReadMessageIndex;
      }

      state.messages = messages;
      state.authors = { ...state.authors, ...authors };
    });
    builder.addCase(getMessages.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_MESSAGES] = { type, status };
    });

    builder.addCase(getPrevMessages.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(getPrevMessages.fulfilled, (state, action) => {
      const { messages, authors } = action.payload;
      const { lastReadMessageIndex } = state.settings;
      const { length } = messages;

      const status =
        length === SMALL_DOWNLOAD_MESSAGES_COUNT
          ? REQUEST_STATUSES.PENDING
          : REQUEST_STATUSES.SUCCESS;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] = status;
      state.settings.lastReadMessageIndex = lastReadMessageIndex + length;
      state.messages = [...messages, ...state.messages];
      state.authors = { ...state.authors, ...authors };
    });
    builder.addCase(getPrevMessages.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] = {
        type,
        status,
      };
    });

    builder.addCase(getNextMessages.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_NEXT_MESSAGES] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(getNextMessages.fulfilled, (state, action) => {
      const { messages, authors, lastReadMessageIndex } = action.payload;

      const status =
        messages.length === SMALL_DOWNLOAD_MESSAGES_COUNT
          ? REQUEST_STATUSES.PENDING
          : REQUEST_STATUSES.SUCCESS;

      if (lastReadMessageIndex > -1) {
        state.settings.lastReadMessageIndex = lastReadMessageIndex;
      }

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_NEXT_MESSAGES] = status;

      const messageSet = new Set(state.messages.map((msg) => msg.id));
      const updatedMessages = [];

      for (const message of messages) {
        if (!messageSet.has(message.id)) {
          updatedMessages.push(message);
        }
      }

      state.messages = [...state.messages, ...updatedMessages];

      state.authors = { ...state.authors, ...authors };
    });
    builder.addCase(getNextMessages.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_NEXT_MESSAGES] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_NEXT_MESSAGES] = {
        type,
        status,
      };
    });

    // Получение списка для перехода по цепочке ответных сообщений
    builder.addCase(getMessagesByReply.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES_BY_REPLY] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(getMessagesByReply.fulfilled, (state, action) => {
      const { messages, authors, lastReadMessageIndex } = action.payload;

      // Сбрасываем статусы для загрузки сообщений вверх и вниз списка
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES] =
        REQUEST_STATUSES.PENDING;
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_PREV_MESSAGES] =
        REQUEST_STATUSES.PENDING;
      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_NEXT_MESSAGES] =
        REQUEST_STATUSES.PENDING;
      // --------------------------------------------------------------

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES_BY_REPLY] =
        REQUEST_STATUSES.SUCCESS;

      state.messages = messages;
      state.authors = { ...state.authors, ...authors };
      state.settings.lastReadMessageIndex = lastReadMessageIndex;
    });
    builder.addCase(getMessagesByReply.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.GET_MESSAGES_BY_REPLY] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.GET_MESSAGES_BY_REPLY] = {
        type,
        status,
      };
    });

    // Отправка сообщения
    builder.addCase(postMessageText.pending, (state, action) => {
      const { traceId } = action.meta.arg.message;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.LOADING;

      // находим сообщение в store и меняем статус сообщения на SEND
      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (messageIndex > -1) {
        const messageFromStore = state.messages[messageIndex];
        const message = { ...messageFromStore, status: MESSAGE_STATUSES.SEND };

        const filteredMessages = state.messages.filter(
          ({ traceId: tId }) => tId !== message.traceId
        );

        state.messages = [...filteredMessages, message];
      }
    });
    builder.addCase(postMessageText.fulfilled, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.SUCCESS;
    });
    builder.addCase(postMessageText.rejected, (state, action) => {
      const { traceId } = action.meta.arg.message;
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.POST_MESSAGE] = { type, status };

      // находим сообщение в store и меняем статус сообщения на ERROR
      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (state.messages && messageIndex > -1) {
        state.messages[messageIndex].status = MESSAGE_STATUSES.ERROR;
      }
    });

    // Отправка сообщения файла
    builder.addCase(postMessageFile.pending, (state, action) => {
      const { traceId } = action.meta.arg;

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.LOADING;

      // находим сообщение в store и меняем статус сообщения на SEND
      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (
        messageIndex > -1 &&
        messageIndex !== getLastIndex(state.messages) &&
        isMessageError(state.messages[messageIndex].status)
      ) {
        const message = {
          ...state.messages[messageIndex],
          status: MESSAGE_STATUSES.SEND,
        };

        const filteredMessages = state.messages.filter(
          ({ traceId: tId }) => tId !== message.traceId
        );

        state.messages = [...filteredMessages, message];
      }
    });
    builder.addCase(postMessageFile.fulfilled, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.SUCCESS;
    });
    builder.addCase(postMessageFile.rejected, (state, action) => {
      const { traceId } = action.meta.arg;
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_MESSAGE] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.POST_MESSAGE] = { type, status };

      // находим сообщение в store и меняем статус сообщения на ERROR
      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (state.messages && messageIndex > -1) {
        state.messages[messageIndex].status = MESSAGE_STATUSES.ERROR;
      }
    });

    // Отправка id последнего прочитанного сообщения
    builder.addCase(postEventMessageReading.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_MESSAGE_READING] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(postEventMessageReading.fulfilled, (state, action) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_MESSAGE_READING] =
        REQUEST_STATUSES.SUCCESS;

      // После успешной отправки ивента
      // помечаем все сообщения оппонентов прочитанными
      // чтобы измежать вызова лишних коллбеков
      // TODO подумать как избежать let
      let switcher = true;
      const { lastReadMessageId } = action.meta.arg;

      state.messages.forEach((message, index) => {
        if (isMessageReceived(message.status) && switcher) {
          state.messages[index].status = MESSAGE_STATUSES.READ;
        }

        if (message.id === lastReadMessageId) {
          switcher = false;
        }
      });
    });
    builder.addCase(postEventMessageReading.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_MESSAGE_READING] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.POST_EVENT_MESSAGE_READING] = {
        type,
        status,
      };
    });

    // Отправка уведомления о том, что юзер набирает сообщение
    builder.addCase(postEventUserTyping.pending, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_USER_TYPING] =
        REQUEST_STATUSES.LOADING;
    });
    builder.addCase(postEventUserTyping.fulfilled, (state) => {
      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_USER_TYPING] =
        REQUEST_STATUSES.SUCCESS;
    });
    builder.addCase(postEventUserTyping.rejected, (state, action) => {
      const { message = DEFAULT_ERROR_TEXT } = action.error;
      const [type, status] = message.split("/");

      state.requestStatuses[CHAT_ENDPOINT_NAMES.POST_EVENT_USER_TYPING] =
        REQUEST_STATUSES.ERROR;
      state.errorMessages[CHAT_ENDPOINT_NAMES.POST_EVENT_USER_TYPING] = {
        type,
        status,
      };
    });

    // обновление статусов прочитанных сообщений
    builder.addCase(updateMessageStatuses.fulfilled, (state, action) => {
      const { messages, lastReadMessageIndex } = action.payload;

      state.messages = messages;

      if (state.messages && lastReadMessageIndex > -1) {
        state.settings.lastReadMessageIndex = lastReadMessageIndex;
      }
    });

    // обновление статуса проверки файла
    builder.addCase(updateFileMessage, (state, action) => {
      const { message } = action.payload;
      const messageIndex = state.messages.findIndex(
        ({ id }) => id === message.id
      );

      if (state.messages && messageIndex > -1) {
        state.messages[messageIndex] = message;
      }
    });

    builder.addCase(setOpponentLastTypingTime, (state, action) => {
      const { date } = action.payload;

      state.settings.opponentLastTypingTime = date;
    });

    // Скачивание файла
    builder.addCase(getFile.pending, (state, action) => {
      const { traceId } = action.meta.arg;

      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (state.messages && messageIndex > -1) {
        const message = state.messages[messageIndex];

        if (isMessageFile(message)) {
          message.content.file.downloading = DOWNLOAD_TYPES.DOWNLOADING;
        }
      }
    });
    builder.addCase(getFile.fulfilled, (state, action) => {
      const { traceId } = action.meta.arg;

      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (state.messages && messageIndex > -1) {
        const message = state.messages[messageIndex];

        if (isMessageFile(message)) {
          message.content.file.downloading = DOWNLOAD_TYPES.DOWNLOADED;
        }
      }
    });
    builder.addCase(getFile.rejected, (state, action) => {
      const { traceId } = action.meta.arg;

      const messageIndex = state.messages.findIndex(
        ({ traceId: tId }) => tId === traceId
      );

      if (state.messages && messageIndex > -1) {
        const message = state.messages[messageIndex];

        if (isMessageFile(message)) {
          message.content.file.downloading = DOWNLOAD_TYPES.DOWNLOADING_ERROR;
        }
      }
    });
    builder.addCase(setOperatorName, (state, action) => {
      const { operatorName } = action.payload;

      state.settings.operatorName = operatorName;
    });
    builder.addCase(setTabTitleUpdateIntervalId, (state, action) => {
      const { intervalId } = action.payload;

      state.settings.tabTitleUpdateIntervalId = intervalId;
    });
  },
});

export const chatReducer = chatSlice.reducer;
